import { z } from 'zod'

import type {
  OnboardingLocationPayrollRules,
  OnboardingLocationPayrollRulesCarBenefit,
  OnboardingLocationPayrollRulesCarBenefitDeserialized,
  OnboardingLocationPayrollRulesHousingBenefit,
  OnboardingLocationPayrollRulesHousingBenefitDeserialized,
  OnboardingLocationPayrollRulesOtherBenefit,
  OnboardingLocationPayrollRulesOtherBenefitDeserialized,
  OnboardingLocationPayrollRulesLeave,
  OnboardingLocationPayrollRulesGarnishmentDeserialized,
  OnboardingLocationPayrollRulesGarnishment,
  OnboardingLocationPayrollRulesAlimony,
  OnboardingLocationPayrollRulesAlimonyDeserialized
} from './PayrollRules.schema'

export const payrollRulesModel = {
  /**
   *
   */
  car_benefit_details: {
    getByIndex: (opts: {
      data: OnboardingLocationPayrollRules
      index: number
    }) => {
      return opts.data.car_benefit_details?.[opts.index] || null
    },
    serialize: (item: OnboardingLocationPayrollRulesCarBenefitDeserialized) => {
      return {
        ...item,
        monthly_amount: Number(item.monthly_amount)
      }
    },
    deserialize: (item?: Partial<OnboardingLocationPayrollRulesCarBenefit>) => {
      return {
        employee_combo_hris_id: item?.employee_combo_hris_id || '',
        monthly_amount: String(item?.monthly_amount || ''),
        description: item?.description || ''
      }
    },
    add: (
      data: OnboardingLocationPayrollRulesCarBenefit[],
      item: OnboardingLocationPayrollRulesCarBenefitDeserialized
    ) => {
      return [...data, payrollRulesModel.car_benefit_details.serialize(item)]
    },
    update: (
      data: OnboardingLocationPayrollRulesCarBenefit[],
      item: OnboardingLocationPayrollRulesCarBenefitDeserialized
    ) => {
      return data.map(i =>
        i.employee_combo_hris_id === item.employee_combo_hris_id
          ? payrollRulesModel.car_benefit_details.serialize(item)
          : i
      )
    },
    remove: (
      data: OnboardingLocationPayrollRulesCarBenefit[],
      index: number
    ) => {
      return data.filter((_, idx) => idx !== index)
    },
    schema: z.object({
      employee_combo_hris_id: z.string().nonempty(),
      monthly_amount: z.string().nonempty(),
      description: z.string().optional()
    })
  },

  /**
   *
   */
  housing_benefit_details: {
    getByIndex: (opts: {
      data: OnboardingLocationPayrollRules
      index: number
    }) => {
      return opts.data.housing_benefit_details?.[opts.index] || null
    },
    serialize: (
      item: OnboardingLocationPayrollRulesHousingBenefitDeserialized
    ) => {
      return {
        ...item,
        monthly_amount: Number(item.monthly_amount)
      }
    },
    deserialize: (
      item?: Partial<OnboardingLocationPayrollRulesHousingBenefit>
    ) => {
      return {
        employee_combo_hris_id: item?.employee_combo_hris_id || '',
        monthly_amount: String(item?.monthly_amount || ''),
        description: item?.description || ''
      }
    },
    add: (
      data: OnboardingLocationPayrollRulesHousingBenefit[],
      item: OnboardingLocationPayrollRulesHousingBenefitDeserialized
    ) => {
      return [
        ...data,
        payrollRulesModel.housing_benefit_details.serialize(item)
      ]
    },
    update: (
      data: OnboardingLocationPayrollRulesHousingBenefit[],
      item: OnboardingLocationPayrollRulesHousingBenefitDeserialized
    ) => {
      return data.map(i =>
        i.employee_combo_hris_id === item.employee_combo_hris_id
          ? payrollRulesModel.housing_benefit_details.serialize(item)
          : i
      )
    },
    remove: (
      data: OnboardingLocationPayrollRulesHousingBenefit[],
      index: number
    ) => {
      return data.filter((_, idx) => idx !== index)
    },
    schema: z.object({
      employee_combo_hris_id: z.string().nonempty(),
      monthly_amount: z.string().nonempty(),
      description: z.string().optional()
    })
  },

  /**
   *
   */
  other_benefits: {
    getByIndex: (opts: {
      data: OnboardingLocationPayrollRules
      index: number
    }) => {
      return opts.data.other_benefits?.[opts.index] || null
    },
    serialize: (
      item: OnboardingLocationPayrollRulesOtherBenefitDeserialized
    ) => {
      return {
        ...item,
        monthly_amount: Number(item.monthly_amount)
      }
    },
    deserialize: (
      item?: Partial<OnboardingLocationPayrollRulesOtherBenefit>
    ) => {
      return {
        employee_combo_hris_id: item?.employee_combo_hris_id || '',
        description: item?.description || '',
        monthly_amount: String(item?.monthly_amount || '')
      }
    },
    add: (
      data: OnboardingLocationPayrollRulesOtherBenefit[],
      item: OnboardingLocationPayrollRulesOtherBenefitDeserialized
    ) => {
      return [...data, payrollRulesModel.other_benefits.serialize(item)]
    },
    update: (
      data: OnboardingLocationPayrollRulesOtherBenefit[],
      item: OnboardingLocationPayrollRulesOtherBenefitDeserialized
    ) => {
      return data.map(i =>
        i.employee_combo_hris_id === item.employee_combo_hris_id
          ? payrollRulesModel.other_benefits.serialize(item)
          : i
      )
    },
    remove: (
      data: OnboardingLocationPayrollRulesOtherBenefit[],
      index: number
    ) => {
      return data.filter((_, idx) => idx !== index)
    },
    schema: z.object({
      employee_combo_hris_id: z.string().nonempty(),
      monthly_amount: z.string().nonempty(),
      description: z.string().optional()
    })
  },

  /**
   *
   */
  current_leaves: {
    getByIndex: (opts: {
      data: OnboardingLocationPayrollRules
      index: number
    }) => {
      return opts.data.current_leaves?.[opts.index] || null
    },
    deserialize: (item?: Partial<OnboardingLocationPayrollRulesLeave>) => {
      return {
        employee_combo_hris_id: item?.employee_combo_hris_id || '',
        start_date: item?.start_date || '',
        end_date: item?.end_date || '',
        type: item?.type || 'sick_leave'
      }
    },
    add: (
      data: OnboardingLocationPayrollRulesLeave[],
      item: OnboardingLocationPayrollRulesLeave
    ) => {
      return [...data, item]
    },
    update: (
      data: OnboardingLocationPayrollRulesLeave[],
      item: OnboardingLocationPayrollRulesLeave
    ) => {
      return data.map(i =>
        i.employee_combo_hris_id === item.employee_combo_hris_id ? item : i
      )
    },
    remove: (data: OnboardingLocationPayrollRulesLeave[], index: number) => {
      return data.filter((_, idx) => idx !== index)
    },
    schema: z.object({
      employee_combo_hris_id: z.string().nonempty(),
      start_date: z.string().nonempty(),
      end_date: z.string().nonempty(),
      type: z.string().nonempty()
    })
  },

  /**
   *
   */
  garnishment_details: {
    getByIndex: (opts: {
      data: OnboardingLocationPayrollRules
      index: number
    }) => {
      return opts.data.garnishment_details?.[opts.index] || null
    },

    serialize: (
      item: OnboardingLocationPayrollRulesGarnishmentDeserialized
    ) => {
      return {
        ...item,
        initial_amount: Number(item.initial_amount),
        remaining_amount: Number(item.remaining_amount)
      }
    },
    deserialize: (
      item?: Partial<OnboardingLocationPayrollRulesGarnishment>
    ) => {
      return {
        employee_combo_hris_id: item?.employee_combo_hris_id || '',
        initial_amount: String(item?.initial_amount || ''),
        remaining_amount: String(item?.remaining_amount || ''),
        comments: item?.comments || ''
      }
    },
    add: (
      data: OnboardingLocationPayrollRulesGarnishment[],
      item: OnboardingLocationPayrollRulesGarnishmentDeserialized
    ) => {
      return [...data, payrollRulesModel.garnishment_details.serialize(item)]
    },
    update: (
      data: OnboardingLocationPayrollRulesGarnishment[],
      item: OnboardingLocationPayrollRulesGarnishmentDeserialized
    ) => {
      return data.map(i =>
        i.employee_combo_hris_id === item.employee_combo_hris_id
          ? payrollRulesModel.garnishment_details.serialize(item)
          : i
      )
    },
    remove: (
      data: OnboardingLocationPayrollRulesGarnishment[],
      index: number
    ) => {
      return data.filter((_, idx) => idx !== index)
    },
    schema: z.object({
      employee_combo_hris_id: z.string().nonempty(),
      initial_amount: z.string().nonempty(),
      remaining_amount: z.string().nonempty(),
      comments: z.string().optional()
    })
  },

  /**
   *
   */
  alimony_details: {
    getByIndex: (opts: {
      data: OnboardingLocationPayrollRules
      index: number
    }) => {
      return opts.data.alimony_details?.[opts.index] || null
    },
    serialize: (item: OnboardingLocationPayrollRulesAlimonyDeserialized) => {
      return {
        ...item,
        initial_amount: Number(item.initial_amount),
        remaining_amount: Number(item.remaining_amount),
        dependents: Number(item.dependents)
      }
    },
    deserialize: (item?: Partial<OnboardingLocationPayrollRulesAlimony>) => {
      return {
        employee_combo_hris_id: item?.employee_combo_hris_id || '',
        initial_amount: String(item?.initial_amount ?? ''),
        remaining_amount: String(item?.remaining_amount ?? ''),
        dependents: String(item?.dependents ?? ''),
        comments: item?.comments || ''
      }
    },
    add: (
      data: OnboardingLocationPayrollRulesAlimony[],
      item: OnboardingLocationPayrollRulesAlimonyDeserialized
    ) => {
      return [...data, payrollRulesModel.alimony_details.serialize(item)]
    },
    update: (
      data: OnboardingLocationPayrollRulesAlimony[],
      item: OnboardingLocationPayrollRulesAlimonyDeserialized
    ) => {
      return data.map(i =>
        i.employee_combo_hris_id === item.employee_combo_hris_id
          ? payrollRulesModel.alimony_details.serialize(item)
          : i
      )
    },
    remove: (data: OnboardingLocationPayrollRulesAlimony[], index: number) => {
      return data.filter((_, idx) => idx !== index)
    },
    schema: z.object({
      employee_combo_hris_id: z.string().nonempty(),
      initial_amount: z.string().nonempty(),
      remaining_amount: z.string().nonempty(),
      dependents: z.string().nonempty(),
      comments: z.string().optional()
    })
  }
}
